import type { DriverFormValues } from '../types'
import { useCallback } from 'react'
import { setCrudSelection, useListApi, useResetEditingState } from '@/atoms'
import { createDriver, updateDriver } from '@/features/domain/driver'
import { useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'
import { emptyDriverId } from '../formState'
import { parseDriverFromFormValues } from '../utils/parseDriverFromFormValues'

export function useOnSubmit() {
  const stopEditing = useResetEditingState()
  const { scrollTo } = useListApi('drivers')
  const dispatch = useAppDispatch()
  const toast = useNotification()

  const onCreate = useCallback(
    async (formValues: DriverFormValues) => {
      try {
        const driver = parseDriverFromFormValues(formValues, true)
        const thunkResult = await dispatch(createDriver(driver))

        if (!createDriver.fulfilled.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        stopEditing()
        setCrudSelection('drivers', [thunkResult.payload])

        scrollTo(thunkResult.payload)
      } catch (e) {
        toast.error(e.message)
      }
    },
    [dispatch, stopEditing, scrollTo, toast],
  )

  const onUpdate = useCallback(
    async (formValues: DriverFormValues) => {
      try {
        const driver = parseDriverFromFormValues(formValues, false)
        const thunkResult = await dispatch(updateDriver(driver))

        if (!updateDriver.fulfilled.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        stopEditing()
      } catch (e) {
        toast.error(e.message)
      }
    },
    [dispatch, stopEditing, toast],
  )

  return useCallback(
    (formState: DriverFormValues) => {
      return formState.id === emptyDriverId ? onCreate(formState) : onUpdate(formState)
    },
    [onCreate, onUpdate],
  )
}
